import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavBar } from '../components/NavBar';
import { api } from '../lib/api';
import { TaskDetail } from './TaskDetail';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Home, Task } from '@home-guard/shared';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const URGENCY_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#9ca3af',
};

function groupByMonth(tasks: Task[], year: number): Map<number, Task[]> {
  const months = new Map<number, Task[]>();
  for (const task of tasks) {
    const d = new Date(task.due_date + 'T00:00:00');
    if (d.getFullYear() === year) {
      const m = d.getMonth();
      const arr = months.get(m) ?? [];
      arr.push(task);
      months.set(m, arr);
    }
  }
  return months;
}

export function YearlyCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });

  const homes = homesData?.data ?? [];
  const primaryHome = homes.find((h) => h.is_primary) ?? homes[0];

  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['tasks', primaryHome?.id, 'pending'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${primaryHome!.id}/tasks?filter=pending`),
    enabled: !!primaryHome,
  });

  const { data: overdueData } = useQuery({
    queryKey: ['tasks', primaryHome?.id, 'overdue'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${primaryHome!.id}/tasks?filter=overdue`),
    enabled: !!primaryHome,
  });

  const allTasks = [...(pendingData?.data ?? []), ...(overdueData?.data ?? [])];
  const monthMap = groupByMonth(allTasks, year);
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const completeMutation = useMutation({
    mutationFn: (taskId: string) => api.put<Task>(`/tasks/${taskId}/complete`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['homes'] });
      setDetailTask(null);
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: (taskId: string) => api.put<Task>(`/tasks/${taskId}/snooze`, { snooze_days: 7 }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setDetailTask(null);
    },
  });

  const selectedTasks = selectedMonth !== null ? (monthMap.get(selectedMonth) ?? []) : [];

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '48px 20px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => { setYear(y => y - 1); setSelectedMonth(null); }}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 10,
              width: 36, height: 36, fontSize: 18, cursor: 'pointer',
              color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Previous year"
          >
            ‹
          </button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
            {year} Calendar
          </h1>
          <button
            onClick={() => { setYear(y => y + 1); setSelectedMonth(null); }}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 10,
              width: 36, height: 36, fontSize: 18, cursor: 'pointer',
              color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Next year"
          >
            ›
          </button>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Critical', color: '#ef4444' },
            { label: 'High', color: '#f97316' },
            { label: 'Medium', color: '#f59e0b' },
            { label: 'Low', color: '#9ca3af' },
          ].map(({ label, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 11, color: '#6b7280' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {pendingLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spinner />
          </div>
        ) : (
          <>
            {/* 12-month grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 10,
              marginBottom: 20,
            }}>
              {MONTH_NAMES.map((name, idx) => {
                const monthTasks = monthMap.get(idx) ?? [];
                const isCurrentMonth = year === currentYear && idx === currentMonth;
                const isSelected = selectedMonth === idx;
                const hasTasks = monthTasks.length > 0;
                const criticalCount = monthTasks.filter(t => t.urgency === 'critical').length;
                const highCount = monthTasks.filter(t => t.urgency === 'high').length;
                const mediumCount = monthTasks.filter(t => t.urgency === 'medium').length;
                const lowCount = monthTasks.filter(t => t.urgency === 'low').length;

                return (
                  <button
                    key={name}
                    onClick={() => setSelectedMonth(isSelected ? null : idx)}
                    style={{
                      background: isSelected ? '#eff6ff' : '#fff',
                      borderRadius: 14,
                      padding: '12px 10px',
                      border: isSelected
                        ? '2px solid #1D4ED8'
                        : isCurrentMonth
                        ? '2px solid #93c5fd'
                        : '2px solid transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                      transition: 'all 0.15s',
                      minHeight: 80,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700,
                        color: isSelected ? '#1D4ED8' : isCurrentMonth ? '#1D4ED8' : '#374151',
                      }}>
                        {name.slice(0, 3)}
                      </span>
                      {hasTasks && (
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: isSelected ? '#1D4ED8' : '#6b7280',
                        }}>
                          {monthTasks.length}
                        </span>
                      )}
                    </div>

                    {hasTasks ? (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {criticalCount > 0 && <UrgencyPip count={criticalCount} color="#ef4444" />}
                        {highCount > 0 && <UrgencyPip count={highCount} color="#f97316" />}
                        {mediumCount > 0 && <UrgencyPip count={mediumCount} color="#f59e0b" />}
                        {lowCount > 0 && <UrgencyPip count={lowCount} color="#9ca3af" />}
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: '#d1d5db' }}>–</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected month task list */}
            {selectedMonth !== null && (
              <div>
                <h2 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: '#111827' }}>
                  {MONTH_NAMES[selectedMonth]} {year}
                  <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 14, marginLeft: 8 }}>
                    {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''}
                  </span>
                </h2>

                {selectedTasks.length === 0 ? (
                  <div style={{
                    background: '#f0fdf4', borderRadius: 14,
                    padding: '24px', textAlign: 'center',
                    border: '1px solid #bbf7d0',
                  }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
                    <p style={{ margin: 0, color: '#166534', fontWeight: 600, fontSize: 14 }}>
                      No tasks due this month
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedTasks
                      .slice()
                      .sort((a, b) => a.due_date.localeCompare(b.due_date))
                      .map((task) => (
                        <CalendarTaskRow
                          key={task.id}
                          task={task}
                          onClick={() => setDetailTask(task)}
                        />
                      ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <NavBar />

      {detailTask && (
        <TaskDetail
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onComplete={() => completeMutation.mutate(detailTask.id)}
          onSnooze={() => snoozeMutation.mutate(detailTask.id)}
          completing={completeMutation.isPending && completeMutation.variables === detailTask.id}
          snoozing={snoozeMutation.isPending && snoozeMutation.variables === detailTask.id}
        />
      )}
    </div>
  );
}

function UrgencyPip({ count, color }: { count: number; color: string }) {
  return (
    <div style={{
      background: color + '22',
      color: color,
      fontSize: 10, fontWeight: 700,
      padding: '1px 5px', borderRadius: 8,
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: color }} />
      {count}
    </div>
  );
}

function CalendarTaskRow({ task, onClick }: { task: Task; onClick: () => void }) {
  const color = URGENCY_COLOR[task.urgency] ?? '#9ca3af';
  const day = new Date(task.due_date + 'T00:00:00').getDate();

  return (
    <button
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 12,
        padding: '12px 14px',
        border: 'none', cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        display: 'flex', alignItems: 'center', gap: 12,
        textAlign: 'left', width: '100%',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: color + '15',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{day}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 14, fontWeight: 600, color: '#111827',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {task.title}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
          {task.category}
        </p>
      </div>
      <span style={{ fontSize: 16, color: '#d1d5db', flexShrink: 0 }}>›</span>
    </button>
  );
}

function Spinner() {
  return (
    <>
      <div style={{
        width: 28, height: 28,
        border: '3px solid #1D4ED8', borderTopColor: 'transparent',
        borderRadius: '50%', animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
